import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

interface ServiceItem {
  icon: string;
  title: string;
  desc: string;
  color: string;
  featured: boolean;
}

const ServicesOverview = () => {
  const { t } = useTranslation();
  const { role } = useAuth();
  const isAdmin = role === 'admin';

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);

  const [content, setContent] = useState({
    badge: '',
    titlePrefix: '',
    titleHighlight: '',
    subtitle: '',
    services: [] as ServiceItem[]
  });

  useEffect(() => {
    // Cargar traducciones por defecto
    const defaultServices: ServiceItem[] = [
      { icon: 'ri-global-line', title: t('home.services.items.web.title'), desc: t('home.services.items.web.desc'), color: '#00c2ff', featured: true },
      { icon: 'ri-customer-service-2-line', title: t('home.services.items.crm.title'), desc: t('home.services.items.crm.desc'), color: '#FF2DAA', featured: false },
      { icon: 'ri-palette-line', title: t('home.services.items.branding.title'), desc: t('home.services.items.branding.desc'), color: '#8c52ff', featured: false },
      { icon: 'ri-instagram-line', title: t('home.services.items.socialConsulting.title'), desc: t('home.services.items.socialConsulting.desc'), color: '#FF2DAA', featured: false },
      { icon: 'ri-video-line', title: t('home.services.items.content.title'), desc: t('home.services.items.content.desc'), color: '#00c2ff', featured: false },
      { icon: 'ri-bar-chart-box-line', title: t('home.services.items.optimization.title'), desc: t('home.services.items.optimization.desc'), color: '#8c52ff', featured: false },
      { icon: 'ri-map-pin-2-line', title: t('home.services.items.google.title'), desc: t('home.services.items.google.desc'), color: '#FF2DAA', featured: false },
      { icon: 'ri-robot-line', title: t('home.services.items.automation.title'), desc: t('home.services.items.automation.desc'), color: '#00c2ff', featured: false },
      { icon: 'ri-lightbulb-line', title: t('home.services.items.strategy.title'), desc: t('home.services.items.strategy.desc'), color: '#8c52ff', featured: false },
      { icon: 'ri-briefcase-4-line', title: t('home.services.items.business.title'), desc: t('home.services.items.business.desc'), color: '#FF2DAA', featured: false },
    ];

    setContent({
      badge: t('home.services.badge'),
      titlePrefix: t('home.services.titlePrefix'),
      titleHighlight: t('home.services.titleHighlight'),
      subtitle: t('home.services.subtitle'),
      services: defaultServices
    });

    const fetchContent = async () => {
      try {
        const docRef = doc(db, 'landing_content', 'services');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setContent(docSnap.data() as typeof content);
        } else {
          // Si no existe, lo creamos con los valores por defecto
          await setDoc(docRef, {
            badge: t('home.services.badge'),
            titlePrefix: t('home.services.titlePrefix'),
            titleHighlight: t('home.services.titleHighlight'),
            subtitle: t('home.services.subtitle'),
            services: defaultServices
          });
        }
      } catch (error) {
        console.error('Error fetching services content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [t]);

  const handleSave = async () => {
    try {
      await setDoc(doc(db, 'landing_content', 'services'), content);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving services content:', error);
      alert('Error al guardar los cambios.');
    }
  };

  const handleServiceChange = (index: number, field: keyof ServiceItem, value: string) => {
    const newServices = content.services.map((service, i) => 
      i === index ? { ...service, [field]: value } : service
    );
    setContent({ ...content, services: newServices });
  };

  return (
    <section className="relative py-24 bg-[#050008] overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_100%,rgba(0,194,255,0.08)_0%,transparent_60%)] pointer-events-none" />

      {/* Botones de Control para Admin */}
      {isAdmin && (
        <div className="absolute top-20 right-4 z-50 flex gap-2">
          {!isEditing ? (
            <button 
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm shadow-lg"
            >
              <i className="ri-edit-line" />
              Editar Sección
            </button>
          ) : (
            <>
              <button 
                onClick={handleSave}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm shadow-lg"
              >
                <i className="ri-save-line" />
                Guardar
              </button>
              <button 
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm shadow-lg"
              >
                <i className="ri-close-line" />
                Cancelar
              </button>
            </>
          )}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          {isEditing ? (
            <div className="max-w-xl mx-auto space-y-3 mb-4">
              <input 
                type="text" 
                value={content.badge} 
                onChange={(e) => setContent({...content, badge: e.target.value})}
                className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-1 text-[#00c2ff] text-xs font-bold uppercase text-center focus:outline-none"
              />
              <div className="flex gap-2">
                <input 
                  type="text" 
                  value={content.titlePrefix} 
                  onChange={(e) => setContent({...content, titlePrefix: e.target.value})}
                  className="w-1/2 bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white text-2xl font-black text-right focus:outline-none"
                />
                <input 
                  type="text" 
                  value={content.titleHighlight} 
                  onChange={(e) => setContent({...content, titleHighlight: e.target.value})}
                  className="w-1/2 bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-[#00c2ff] text-2xl font-black text-left focus:outline-none"
                />
              </div>
              <textarea 
                value={content.subtitle} 
                onChange={(e) => setContent({...content, subtitle: e.target.value})}
                className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white/50 text-sm text-center focus:outline-none h-20"
              />
            </div>
          ) : (
            <>
              <p className="text-[#00c2ff] text-xs font-bold uppercase tracking-widest mb-3">{content.badge}</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4">
                {content.titlePrefix}{' '}
                <span className="bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] bg-clip-text text-transparent">
                  {content.titleHighlight}
                </span>
              </h2>
              <p className="text-white/50 text-base md:text-lg max-w-2xl mx-auto">
                {content.subtitle}
              </p>
            </>
          )}
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {content.services.map((service, i) => {
            const to = service.title === t('home.services.items.business.title') ? '/business-consulting' : '/services';
            return (
              <div
                key={i}
                className={`group relative backdrop-blur-sm rounded-2xl p-6 transition-all duration-500 hover:-translate-y-2 overflow-hidden ${
                  service.featured
                    ? 'sm:col-span-2 border border-[#00c2ff]/30 bg-gradient-to-br from-[#00c2ff]/8 to-[#8c52ff]/8'
                    : 'bg-white/3 border border-white/8 hover:border-white/20'
                }`}
                style={{ animationDelay: `${i * 80}ms` }}
              >
                {/* Hover glow */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl pointer-events-none"
                  style={{ background: `radial-gradient(ellipse at 50% 0%, ${service.color}12 0%, transparent 70%)` }}
                />
                {/* Top accent line */}
                <div
                  className={`absolute top-0 left-0 right-0 h-0.5 transition-opacity duration-500 pointer-events-none ${service.featured ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                  style={{ background: `linear-gradient(90deg, transparent, ${service.color}, transparent)` }}
                />

                {service.featured && (
                  <div className="absolute top-4 right-4 flex items-center gap-1.5 bg-[#00c2ff]/15 border border-[#00c2ff]/30 rounded-full px-3 py-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#00c2ff] animate-pulse" />
                    <span className="text-[#00c2ff] text-xs font-bold uppercase tracking-wider">{t('home.services.popular')}</span>
                  </div>
                )}

                <div
                  className="w-12 h-12 flex items-center justify-center rounded-xl mb-4 transition-transform duration-300 group-hover:scale-110"
                  style={{ background: `${service.color}20`, border: `1px solid ${service.color}30` }}
                >
                  <i className={`${service.icon} text-xl`} style={{ color: service.color }} />
                </div>

                {isEditing ? (
                  <div className="space-y-2">
                    <input 
                      type="text" 
                      value={service.title} 
                      onChange={(e) => handleServiceChange(i, 'title', e.target.value)}
                      className="w-full bg-white/5 border border-purple-500/50 rounded px-2 py-1 text-white text-sm font-bold focus:outline-none"
                    />
                    <textarea 
                      value={service.desc} 
                      onChange={(e) => handleServiceChange(i, 'desc', e.target.value)}
                      className="w-full bg-white/5 border border-purple-500/50 rounded px-2 py-1 text-white/50 text-xs focus:outline-none h-16"
                    />
                  </div>
                ) : (
                  <>
                    <h3 className="text-white font-bold text-base mb-2 leading-tight">{service.title}</h3>
                    <p className="text-white/50 text-sm leading-relaxed">{service.desc}</p>

                    <Link to={to} className="mt-4 flex items-center gap-1 text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ color: service.color }}>
                      <span>{t('home.services.more')}</span>
                      <i className="ri-arrow-right-line" />
                    </Link>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* CTA */}
        {!isEditing && (
          <div className="text-center mt-12">
            <Link
              to="/services"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-white/5 border border-white/20 text-white font-semibold px-8 py-4 rounded-full hover:bg-white/10 hover:border-[#8c52ff]/50 transition-all duration-300 cursor-pointer"
            >
              {t('home.services.allButton')}
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default ServicesOverview;
